import { Box } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Header, Row } from '../../../components/Table/TableModels';
import { RootState } from '../../../stores/Store';
import ImportService from '../../../services/Import/ImportService';
import useSnackBar from '../../../hooks/useSnackBar/useSnackBar';
import { UploadType } from '../../../models/ImportModels';

interface ErrorLines {
  reason: number;
  lines: number[];
}

function useImportStep() {
  const [t] = useTranslation();
  const snackBar = useSnackBar();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { importResponse } = useSelector((state: RootState) => state.importReducer);

  /**
   * Requests the confirmation of the duplicated file import
   * @param setOpen
   */
  const confirmDuplicatedFileImport = (uploadType: UploadType, uploadId: string, onClose: () => void) => {
    setIsLoading(true);
    ImportService.api
      .confirmDuplicatedImportsUpload({ uploadId, confirm: true, uploadType })
      .then(
        () => {
          snackBar.showSnackBar(t('pages.import.uploadSuccess'), 'success');
          navigate('/esimmanager');
          onClose();
        },
        () => {
          snackBar.showSnackBar(t('pages.import.confirmImportError'), 'error');
        },
      )
      .finally(() => setIsLoading(false));
  };

  const headers: Header[] = useMemo(
    () => [
      {
        id: 'name',
        label: t('translation:pages.import.line'),
        isSortable: false,
      },

      {
        id: 'issue',
        label: t('pages.import.issue'),
        isSortable: false,
      },
    ],
    [t],
  );

  const rows: Row[] = useMemo(() => {
    if (importResponse && importResponse.errorLines) {
      const errors = new Map<number, ErrorLines>();

      for (let i = 0; i < importResponse.errorLines.length; i += 1) {
        const { line, reasons } = importResponse.errorLines[i];

        for (let j = 0; j < reasons.length; j += 1) {
          const reason = reasons[j];
          if (!errors.has(reason)) {
            errors.set(reason, { reason, lines: [] });
          }
          errors.get(reason)?.lines.push(line);
        }
      }
      return Array.from(errors.values()).map((errorLine) => ({
        id: `row-${errorLine.reason}`,
        cells: [
          {
            value: <Box sx={{ maxWidth: '11.5rem' }}>{errorLine.lines.join(', ')}</Box>,
          },
          {
            value: t(`common.errors.httpReason.${errorLine.reason}`),
          },
        ],
      }));
    }
    return [];
  }, [t, importResponse?.errorLines]);

  return { headers, rows, isLoading, confirmDuplicatedFileImport };
}

export default useImportStep;
